import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiArrowLeft,
  FiClock,
  FiDownload,
  FiEdit,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { ConfirmDialog } from "./common/ConfirmDialog";
import { DeleteFieldModal, ExtraColumnHeader, RenameFieldModal } from "./catalog/CatalogFieldModals";
import { equipmentTimeCardsService } from "../services/api/equipmentTimeCardsService";
import { loadXLSX } from "../utils/xlsxLoader";
import "./StockInventoryItemsManage.css";
import "./EquipmentTimeCardsManage.css";

const CORE_COLUMNS = [
  { key: "equipment_id", label: "Equipment ID" },
  { key: "unit_number", label: "Unit Number" },
  { key: "description", label: "Description" },
  { key: "primary_division", label: "Primary Division" },
  { key: "secondary_division", label: "Secondary Division" },
  { key: "notes", label: "Notes" },
  { key: "crew_leader", label: "Crew Leader" },
];

const EMPTY_FORM = {
  equipment_id: "",
  unit_number: "",
  description: "",
  primary_division: "",
  secondary_division: "",
  notes: "",
  crew_leader: "",
};

function normalizeExtraFields(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const extra = {};
  Object.entries(value).forEach(([key, val]) => {
    const name = String(key || "").trim();
    if (!name) return;
    extra[name] = val == null ? "" : String(val);
  });
  return extra;
}

function extraFieldsToRows(extraFields, extraKeys) {
  const extra = normalizeExtraFields(extraFields);
  const keys = [...extraKeys];
  Object.keys(extra).forEach((key) => {
    if (!keys.includes(key)) keys.push(key);
  });
  if (keys.length === 0) return [{ key: "", value: "" }];
  return keys.map((key) => ({ key, value: extra[key] || "" }));
}

function FieldLabel({ children, required = false }) {
  return (
    <span className="stock-inv-field-name">
      {children}
      {required ? <span className="stock-inv-required">*</span> : null}
    </span>
  );
}

function withCurrentOption(options, current) {
  const value = String(current || "").trim();
  if (!value) return options;
  if (options.some((opt) => opt === value)) return options;
  return [value, ...options];
}

function rowsToExtraFields(rows) {
  const extra = {};
  (rows || []).forEach((row) => {
    const key = String(row.key || "").trim();
    if (!key) return;
    extra[key] = String(row.value || "").trim();
  });
  return extra;
}

function useModalLock(isOpen, onClose) {
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);
}

function CatalogSelect({
  label,
  value,
  options,
  isCustom,
  onSelectChange,
  onCustomChange,
  placeholder,
  required = false,
}) {
  return (
    <>
      <label>
        <FieldLabel required={required}>{label}</FieldLabel>
        <select
          value={isCustom ? "__custom__" : value}
          onChange={onSelectChange}
          required={required}
        >
          <option value="" disabled={required}>
            {placeholder}
          </option>
          {withCurrentOption(options, isCustom ? "" : value).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
          <option value="__custom__">Add new…</option>
        </select>
      </label>
      {isCustom ? (
        <label>
          <FieldLabel required={required}>New {label.toLowerCase()}</FieldLabel>
          <input
            value={value}
            onChange={onCustomChange}
            placeholder={`e.g. ${placeholder}`}
            required={required}
          />
        </label>
      ) : null}
    </>
  );
}

function ItemFormModal({
  isOpen,
  mode,
  initialItem,
  extraKeys,
  primaryDivisionOptions,
  secondaryDivisionOptions,
  crewLeaderOptions,
  columnLabels = {},
  hiddenOptionalKeys = [],
  onClose,
  onSave,
  isSaving,
  error,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [extraRows, setExtraRows] = useState([{ key: "", value: "" }]);
  const [isCustomPrimary, setIsCustomPrimary] = useState(false);
  const [isCustomSecondary, setIsCustomSecondary] = useState(false);
  const [isCustomCrew, setIsCustomCrew] = useState(false);

  useModalLock(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) return;
    if (initialItem) {
      setForm({
        equipment_id: initialItem.equipment_id || "",
        unit_number: initialItem.unit_number || "",
        description: initialItem.description || "",
        primary_division: initialItem.primary_division || "",
        secondary_division: initialItem.secondary_division || "",
        notes: initialItem.notes || "",
        crew_leader: initialItem.crew_leader || "",
      });
      setExtraRows(extraFieldsToRows(initialItem.extra_fields, extraKeys));
      setIsCustomPrimary(
        Boolean(initialItem.primary_division) &&
          !primaryDivisionOptions.some((value) => value === initialItem.primary_division)
      );
      setIsCustomSecondary(
        Boolean(initialItem.secondary_division) &&
          !secondaryDivisionOptions.some((value) => value === initialItem.secondary_division)
      );
      setIsCustomCrew(
        Boolean(initialItem.crew_leader) &&
          !crewLeaderOptions.some((value) => value === initialItem.crew_leader)
      );
    } else {
      setForm(EMPTY_FORM);
      setExtraRows(extraFieldsToRows({}, extraKeys));
      setIsCustomPrimary(primaryDivisionOptions.length === 0);
      setIsCustomSecondary(secondaryDivisionOptions.length === 0);
      setIsCustomCrew(crewLeaderOptions.length === 0);
    }
  }, [
    isOpen,
    initialItem,
    extraKeys,
    primaryDivisionOptions,
    secondaryDivisionOptions,
    crewLeaderOptions,
  ]);

  if (!isOpen) return null;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleCatalogSelect = (field, setCustom) => (event) => {
    const value = event.target.value;
    if (value === "__custom__") {
      setCustom(true);
      updateField(field, "");
      return;
    }
    setCustom(false);
    updateField(field, value);
  };

  const updateExtraRow = (index, patch) => {
    setExtraRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addExtraRow = () => {
    setExtraRows((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeExtraRow = (index) => {
    setExtraRows((prev) => (prev.length <= 1 ? [{ key: "", value: "" }] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSave({
      ...form,
      extra_fields: rowsToExtraFields(extraRows),
    });
  };

  return createPortal(
    <div className="stock-inv-modal-overlay equipment-theme" onClick={onClose} role="presentation">
      <div
        className="stock-inv-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="equipment-tc-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="stock-inv-modal-header">
          <div>
            <h3 id="equipment-tc-modal-title">
              {mode === "edit" ? "Edit Equipment Time Card" : "Add Equipment Time Card"}
            </h3>
            <p>Match the Excel master sheet fields. Extra fields stay on this row.</p>
          </div>
          <button type="button" className="stock-inv-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>

        <form className="stock-inv-form" onSubmit={handleSubmit}>
          <div className="stock-inv-modal-body">
            <div className="stock-inv-form-grid">
              <label>
                <FieldLabel required>{columnLabels.equipment_id || "Equipment ID"}</FieldLabel>
                <input
                  value={form.equipment_id}
                  onChange={(e) => updateField("equipment_id", e.target.value)}
                  required
                />
              </label>
              <label>
                <FieldLabel required>{columnLabels.unit_number || "Unit Number"}</FieldLabel>
                <input
                  value={form.unit_number}
                  onChange={(e) => updateField("unit_number", e.target.value)}
                  required
                />
              </label>
              <label className="stock-inv-span-2">
                <FieldLabel required>{columnLabels.description || "Description"}</FieldLabel>
                <input
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  required
                />
              </label>
              {hiddenOptionalKeys.includes("primary_division") ? null : (
                <CatalogSelect
                  label={columnLabels.primary_division || "Primary Division"}
                  value={form.primary_division}
                  options={primaryDivisionOptions}
                  isCustom={isCustomPrimary}
                  onSelectChange={handleCatalogSelect("primary_division", setIsCustomPrimary)}
                  onCustomChange={(e) => updateField("primary_division", e.target.value)}
                  placeholder="Select primary division"
                />
              )}
              {hiddenOptionalKeys.includes("secondary_division") ? null : (
                <CatalogSelect
                  label={columnLabels.secondary_division || "Secondary Division"}
                  value={form.secondary_division}
                  options={secondaryDivisionOptions}
                  isCustom={isCustomSecondary}
                  onSelectChange={handleCatalogSelect("secondary_division", setIsCustomSecondary)}
                  onCustomChange={(e) => updateField("secondary_division", e.target.value)}
                  placeholder="Select secondary division"
                />
              )}
              {hiddenOptionalKeys.includes("crew_leader") ? null : (
                <CatalogSelect
                  label={columnLabels.crew_leader || "Crew Leader"}
                  value={form.crew_leader}
                  options={crewLeaderOptions}
                  isCustom={isCustomCrew}
                  onSelectChange={handleCatalogSelect("crew_leader", setIsCustomCrew)}
                  onCustomChange={(e) => updateField("crew_leader", e.target.value)}
                  placeholder="Select crew leader"
                />
              )}
              {hiddenOptionalKeys.includes("notes") ? null : (
                <label>
                  <FieldLabel>{columnLabels.notes || "Notes"}</FieldLabel>
                  <input
                    value={form.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                  />
                </label>
              )}
            </div>

            <div className="stock-inv-extra-block">
              <div className="stock-inv-extra-header">
                <div>
                  <h4>Additional fields</h4>
                  <p>Add any extra column that is not in the master Excel sheet.</p>
                </div>
                <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={addExtraRow}>
                  <FiPlus /> Add field
                </button>
              </div>
              <div className="stock-inv-extra-list">
                {extraRows.map((row, index) => (
                  <div key={`${row.key}-${index}`} className="stock-inv-extra-row">
                    <input
                      placeholder="Field name"
                      value={row.key}
                      onChange={(e) => updateExtraRow(index, { key: e.target.value })}
                    />
                    <input
                      placeholder="Value"
                      value={row.value}
                      onChange={(e) => updateExtraRow(index, { value: e.target.value })}
                    />
                    <button
                      type="button"
                      className="stock-inv-icon-btn stock-inv-icon-btn-danger"
                      onClick={() => removeExtraRow(index)}
                      aria-label="Remove field"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {error ? <div className="stock-inv-form-error">{error}</div> : null}
          </div>

          <div className="stock-inv-modal-actions">
            <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="stock-inv-btn stock-inv-btn-primary" disabled={isSaving}>
              {isSaving ? "Saving..." : mode === "edit" ? "Save changes" : "Create item"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function AddFieldModal({ isOpen, onClose, onAdd, existingKeys }) {
  const [fieldName, setFieldName] = useState("");
  const [error, setError] = useState("");

  useModalLock(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      setFieldName("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (event) => {
    event.preventDefault();
    const name = fieldName.trim();
    if (!name) {
      setError("Enter a field name.");
      return;
    }
    const taken = existingKeys.some((key) => key.toLowerCase() === name.toLowerCase());
    if (taken) {
      setError("That field already exists.");
      return;
    }
    onAdd(name);
  };

  return createPortal(
    <div className="stock-inv-modal-overlay equipment-theme" onClick={onClose} role="presentation">
      <div className="stock-inv-modal stock-inv-modal-sm" onClick={(event) => event.stopPropagation()}>
        <div className="stock-inv-modal-header">
          <div>
            <h3>Add field</h3>
            <p>This column will appear on every row. Fill values when you edit a row.</p>
          </div>
          <button type="button" className="stock-inv-icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </div>
        <form className="stock-inv-form" onSubmit={handleSubmit}>
          <div className="stock-inv-modal-body">
            <label>
              <FieldLabel>Field name</FieldLabel>
              <input
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="e.g. Notes"
                autoFocus
              />
            </label>
            {error ? <div className="stock-inv-form-error">{error}</div> : null}
          </div>
          <div className="stock-inv-modal-actions">
            <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="stock-inv-btn stock-inv-btn-primary">
              Add field
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function EquipmentTimeCardsManage({ onBack, onToast }) {
  const [items, setItems] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("");
  const [addedFieldKeys, setAddedFieldKeys] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [isAddFieldOpen, setIsAddFieldOpen] = useState(false);
  const [isRenameFieldOpen, setIsRenameFieldOpen] = useState(false);
  const [renameFieldFrom, setRenameFieldFrom] = useState("");
  const [isRenamingField, setIsRenamingField] = useState(false);
  const [renameFieldError, setRenameFieldError] = useState("");
  const [isDeleteFieldOpen, setIsDeleteFieldOpen] = useState(false);
  const [deleteFieldKey, setDeleteFieldKey] = useState("");
  const [isDeletingField, setIsDeletingField] = useState(false);
  const [deleteFieldError, setDeleteFieldError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const [response, columnsResponse] = await Promise.all([
        equipmentTimeCardsService.listCatalog(),
        equipmentTimeCardsService.listColumns(),
      ]);
      setItems(Array.isArray(response?.data) ? response.data : []);
      setColumns(Array.isArray(columnsResponse?.data) ? columnsResponse.data : []);
    } catch (error) {
      console.error("Failed to load equipment time cards:", error);
      setLoadError(error?.response?.data?.message || "Failed to load equipment time cards.");
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) => col.visible);
    if (visible.length > 0) return visible;
    return CORE_COLUMNS.map((col) => ({ ...col, visible: true, extra: false, required: true }));
  }, [columns]);
  const extraKeys = useMemo(() => {
    const keys = visibleColumns.filter((col) => col.extra).map((col) => col.key);
    const seen = new Set(keys.map((key) => key.toLowerCase()));
    items.forEach((item) => {
      Object.keys(normalizeExtraFields(item.extra_fields)).forEach((key) => {
        const lower = key.toLowerCase();
        if (seen.has(lower)) return;
        seen.add(lower);
        keys.push(key);
      });
    });
    addedFieldKeys.forEach((key) => {
      const lower = key.toLowerCase();
      if (seen.has(lower)) return;
      seen.add(lower);
      keys.push(key);
    });
    return keys;
  }, [items, addedFieldKeys, visibleColumns]);
  const columnLabels = useMemo(() => {
    const labels = {};
    columns.forEach((col) => {
      labels[col.key] = col.label;
    });
    return labels;
  }, [columns]);
  const hiddenOptionalKeys = useMemo(
    () => columns.filter((col) => !col.visible && !col.required && !col.extra).map((col) => col.key),
    [columns]
  );

  const primaryDivisionOptions = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.primary_division).filter(Boolean))).sort();
  }, [items]);
  const secondaryDivisionOptions = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.secondary_division).filter(Boolean))).sort();
  }, [items]);
  const crewLeaderOptions = useMemo(() => {
    return Array.from(new Set(items.map((item) => item.crew_leader).filter(Boolean))).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return items.filter((item) => {
      if (divisionFilter && item.primary_division !== divisionFilter) return false;
      if (!term) return true;
      const extraText = Object.entries(normalizeExtraFields(item.extra_fields))
        .map(([key, value]) => `${key} ${value}`)
        .join(" ");
      const haystack = [
        item.equipment_id,
        item.unit_number,
        item.description,
        item.primary_division,
        item.secondary_division,
        item.notes,
        item.crew_leader,
        extraText,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [items, searchTerm, divisionFilter]);

  const openCreate = () => {
    setFormMode("create");
    setEditingItem(null);
    setFormError("");
    setIsFormOpen(true);
  };

  const openEdit = (item) => {
    setFormMode("edit");
    setEditingItem(item);
    setFormError("");
    setIsFormOpen(true);
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    setFormError("");
    try {
      if (formMode === "edit" && editingItem?.id) {
        await equipmentTimeCardsService.updateItem(editingItem.id, payload);
        onToast?.("Equipment time card updated.");
      } else {
        await equipmentTimeCardsService.createItem(payload);
        onToast?.("Equipment time card created.");
      }
      setIsFormOpen(false);
      setEditingItem(null);
      await loadItems();
    } catch (error) {
      setFormError(error?.response?.data?.message || "Failed to save equipment time card.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await equipmentTimeCardsService.deleteItem(deleteTarget.id);
      onToast?.(`Deleted ${deleteTarget.equipment_id}.`);
      setDeleteTarget(null);
      await loadItems();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to delete equipment time card.");
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportToExcel = async () => {
    if (isExporting || isLoading || filteredItems.length === 0) return;

    try {
      setIsExporting(true);
      const XLSX = await loadXLSX();
      if (!XLSX) {
        onToast?.("Unable to download Excel right now. Please try again.");
        return;
      }

      const headers = visibleColumns.map((col) => col.label);
      const rows = filteredItems.map((item) => {
        const extra = normalizeExtraFields(item.extra_fields);
        return visibleColumns.map((col) => {
          if (col.extra) return extra[col.key] || "";
          return item[col.key] || "";
        });
      });

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EquipmentTimeCards");
      XLSX.writeFile(workbook, "Equipment Time Cards.xlsx");
      onToast?.(`Downloaded ${filteredItems.length} equipment time card${filteredItems.length === 1 ? "" : "s"}.`);
    } catch (error) {
      console.error("Failed to export equipment time cards:", error);
      onToast?.("Failed to download Excel file.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddField = async (name) => {
    try {
      await equipmentTimeCardsService.addColumn(name);
      setAddedFieldKeys((prev) => (prev.includes(name) ? prev : [...prev, name]));
      setIsAddFieldOpen(false);
      onToast?.(`Added field "${name}". Edit a row to fill its value.`);
      await loadItems();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to add field.");
    }
  };

  const openRenameField = (key = "") => {
    if (visibleColumns.length === 0) return;
    setRenameFieldFrom(key);
    setRenameFieldError("");
    setIsRenameFieldOpen(true);
  };

  const openDeleteField = (key = "") => {
    if (visibleColumns.length === 0) return;
    setDeleteFieldKey(key);
    setDeleteFieldError("");
    setIsDeleteFieldOpen(true);
  };

  const handleRenameField = async (from, to) => {
    const current = visibleColumns.find((col) => col.key === from || col.label === from);
    if (current?.label === to) {
      setIsRenameFieldOpen(false);
      return;
    }
    setIsRenamingField(true);
    setRenameFieldError("");
    try {
      await equipmentTimeCardsService.renameExtraField(from, to);
      setAddedFieldKeys((prev) => {
        const next = prev.map((key) => (key.toLowerCase() === from.toLowerCase() ? to : key));
        if (!next.some((key) => key.toLowerCase() === to.toLowerCase())) next.push(to);
        return next.filter((key, index, list) => list.findIndex((item) => item.toLowerCase() === key.toLowerCase()) === index);
      });
      setIsRenameFieldOpen(false);
      onToast?.(`Renamed field to "${to}".`);
      await loadItems();
    } catch (error) {
      setRenameFieldError(error?.response?.data?.message || "Failed to rename field.");
    } finally {
      setIsRenamingField(false);
    }
  };

  const handleDeleteExtraField = async (key) => {
    setIsDeletingField(true);
    setDeleteFieldError("");
    try {
      await equipmentTimeCardsService.deleteExtraField(key);
      setAddedFieldKeys((prev) => prev.filter((name) => name.toLowerCase() !== key.toLowerCase()));
      setIsDeleteFieldOpen(false);
      onToast?.(`Deleted field.`);
      await loadItems();
    } catch (error) {
      setDeleteFieldError(error?.response?.data?.message || "Failed to delete field.");
    } finally {
      setIsDeletingField(false);
    }
  };

  const existingFieldKeys = [
    ...visibleColumns.map((col) => col.label),
    ...visibleColumns.map((col) => col.key),
  ];

  return (
    <div className="config-sub-view fade-in stock-inv-manage equipment-theme">
      <div className="sub-view-nav" onClick={onBack}>
        <FiArrowLeft /> Back to Upload Equipment Time Cards
      </div>

      <header className="sub-view-header stock-inv-header">
        <div className="header-icon-badge">
          <FiClock />
        </div>
        <div className="stock-inv-header-copy">
          <h2>Manage Equipment Time Cards</h2>
          <p>
            Edit the catalog stored in the database. Create, update, or delete rows, and add, rename, or delete any
            column when needed. A later Excel upload must use these column names, in this order.
          </p>
        </div>
        <div className="stock-inv-header-actions">
          <button type="button" className="stock-inv-btn stock-inv-btn-ghost" onClick={() => setIsAddFieldOpen(true)}>
            <FiPlus /> Add field
          </button>
          <button type="button" className="stock-inv-btn stock-inv-btn-primary" onClick={openCreate}>
            <FiPlus /> Add item
          </button>
        </div>
      </header>

      <div className="sub-view-card stock-inv-card">
        <div className="stock-inv-toolbar">
          <div className="stock-inv-search">
            <FiSearch />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search equipment ID, description..."
            />
          </div>
          <select
            className="stock-inv-filter"
            value={divisionFilter}
            onChange={(e) => setDivisionFilter(e.target.value)}
          >
            <option value="">{`All ${(columnLabels.primary_division || "primary division").toLowerCase()}s`}</option>
            {primaryDivisionOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          <div className="stock-inv-count">
            {filteredItems.length} of {items.length} item{items.length === 1 ? "" : "s"}
          </div>
          <button
            type="button"
            className="stock-inv-export-btn"
            onClick={handleExportToExcel}
            disabled={isLoading || isExporting || filteredItems.length === 0}
            title="Download Excel"
            aria-label="Download Excel"
          >
            <FiDownload />
          </button>
        </div>

        <div className="stock-inv-table-wrap">
          <table className="users-table stock-inv-table">
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th key={col.key}>
                    <ExtraColumnHeader
                      columnKey={col.key}
                      name={col.label}
                      onRename={openRenameField}
                      onDelete={openDeleteField}
                    />
                  </th>
                ))}
                <th className="stock-inv-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="users-table-empty">
                    Loading equipment time cards...
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="users-table-empty">
                    {loadError}
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="users-table-empty">
                    No equipment time cards found. Add a row or upload the Excel master sheet.
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const extra = normalizeExtraFields(item.extra_fields);
                  return (
                    <tr key={item.id}>
                      {visibleColumns.map((col) => {
                        if (col.extra) {
                          return <td key={col.key}>{extra[col.key] || "—"}</td>;
                        }
                        if (col.key === "equipment_id") {
                          return (
                            <td key={col.key} className="stock-inv-id-cell">
                              {item.equipment_id}
                            </td>
                          );
                        }
                        if (col.key === "description") {
                          return (
                            <td key={col.key} className="stock-inv-desc-cell">
                              {item.description || "—"}
                            </td>
                          );
                        }
                        if (col.key === "primary_division") {
                          return (
                            <td key={col.key}>
                              {item.primary_division ? (
                                <span className="stock-inv-chip">{item.primary_division}</span>
                              ) : (
                                "—"
                              )}
                            </td>
                          );
                        }
                        return <td key={col.key}>{item[col.key] || "—"}</td>;
                      })}
                      <td className="stock-inv-actions-col">
                        <button
                          type="button"
                          className="stock-inv-icon-btn"
                          title="Edit"
                          onClick={() => openEdit(item)}
                        >
                          <FiEdit />
                        </button>
                        <button
                          type="button"
                          className="stock-inv-icon-btn stock-inv-icon-btn-danger"
                          title="Delete"
                          onClick={() => setDeleteTarget(item)}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ItemFormModal
        isOpen={isFormOpen}
        mode={formMode}
        initialItem={editingItem}
        extraKeys={extraKeys}
        primaryDivisionOptions={primaryDivisionOptions}
        secondaryDivisionOptions={secondaryDivisionOptions}
        crewLeaderOptions={crewLeaderOptions}
        columnLabels={columnLabels}
        hiddenOptionalKeys={hiddenOptionalKeys}
        onClose={() => setIsFormOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
        error={formError}
      />

      <AddFieldModal
        isOpen={isAddFieldOpen}
        onClose={() => setIsAddFieldOpen(false)}
        onAdd={handleAddField}
        existingKeys={existingFieldKeys}
      />

      <RenameFieldModal
        isOpen={isRenameFieldOpen}
        fields={visibleColumns}
        extraKeys={extraKeys}
        initialFrom={renameFieldFrom}
        existingKeys={existingFieldKeys}
        overlayClassName="equipment-theme"
        onClose={() => {
          if (!isRenamingField) setIsRenameFieldOpen(false);
        }}
        onRename={handleRenameField}
        isSaving={isRenamingField}
        error={renameFieldError}
      />

      <DeleteFieldModal
        isOpen={isDeleteFieldOpen}
        fields={visibleColumns}
        extraKeys={extraKeys}
        initialKey={deleteFieldKey}
        overlayClassName="equipment-theme"
        onClose={() => {
          if (!isDeletingField) setIsDeleteFieldOpen(false);
        }}
        onDelete={handleDeleteExtraField}
        isSaving={isDeletingField}
        error={deleteFieldError}
      />

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Delete equipment time card?"
        message={
          deleteTarget
            ? `Delete ${deleteTarget.equipment_id} — ${deleteTarget.description}? This removes it from the master catalog.`
            : ""
        }
        confirmLabel="Delete"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
      />
    </div>
  );
}

export default EquipmentTimeCardsManage;
